import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  Paper,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../services/redux/store";
import { decrypt } from "../../../utils/crypto";
import losServices from "../../../services/api/service/losService";
import eligibilityCheckServices from "../../../services/api/service/eligibilityCheckService";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import { setDocumentApplicantType, setAggregatorDocId } from "../../../services/redux/slices/stepperDynamicFormSlice";
import CustomDivider from "../../../components/common/CustomDivider";
import LoaderBackdrop from "../../../components/ui/LoaderBackdrop";

import { TabPanel } from "./components/TabPanel";
import { ApplicantTabsHeaderActions } from "./components/ApplicantTabsHeaderActions";
import { ApplicantTabsNav } from "./components/ApplicantTabsNav";
import { DocumentsSkeletonPanel } from "./components/DocumentsSkeletonPanel";
import { ApplicantInfoBox } from "./components/ApplicantInfoBox";
import { DocumentsSectionHeader } from "./components/DocumentsSectionHeader";
import { DocumentCategoriesList } from "./components/DocumentCategoriesList";
import { ApplicantTabsModals } from "./components/ApplicantTabsModals";

import { useDocumentsData } from "./hooks/useDocumentsData";
import { useDocumentUpload } from "./hooks/useDocumentUpload";
import { useDocumentPreview } from "./hooks/useDocumentPreview";
import { useBulkUpload } from "./hooks/useBulkUpload";
import { useAccountAggregator } from "./hooks/useAccountAggregator";
import { useDigiLocker } from "./hooks/useDigiLocker";
import { useCoApplicantFlow } from "./hooks/useCoApplicantFlow";
import type { ApplicantTabsProps } from "./types";
import { toAbsoluteUrl } from "../../../utils/helpers";
import { trackCustomerJourneyEvent } from "../../../utils/ga";
import { ROUTES } from "../../../routes/routerConfig";

export default function ApplicantTabs(props: ApplicantTabsProps) {
  const {
    isApplicantTabsLoading,
    applicants,
    tabsListLoading,
    coApplicantDetails,
    applicationId,
    handleFetchApplicationDetail,
    partnerStartedJourney,
    enableCustomerJourney,
    setPartnerStartedJourney,
    hideHeaderActions = false,
    headerActionsPortalTarget = null,
    headerActionsOnly = false,
    disableHeaderActionsPortal = false,
    mainApplicantDetails
  } = props;
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [tabIndex, setTabIndex] = useState(0);
  const [aggregatorLoader, setAggregatorLoader] = useState(false);

  const [isAddMoreModalOpen, setIsAddMoreModalOpen] = useState(false);
  const [modelType, setModelType] = useState<string>("");
  const [isAccountAggregatorModalOpen, setIsAccountAggregatorModalOpen] =
    useState<boolean>(false);
  const [viewDocumentLoader, setViewDocumentLoader] = useState<any[]>([]);
  const [viewBulkDocumentsLoader, setViewBulkDocumentsLoader] = useState(false);
  const [fiSessionId, setFiSessionId] = useState<string>("");
  const [consentApiData, setConsentApiData] = useState<any>(null);

  const aggregatorDocId: any = useSelector(
    (state: RootState) => state.stepper.aggregatorDocId
  );
  const consentApplicationNo: any = useSelector(
    (state: RootState) => state.stepper.consentApplicationNo
  );
  const digiLockerLoaderId: any = useSelector(
    (state: RootState) => state.stepper.digiLockerLoaderId
  );
  const fetchUserApplicationData: any = useSelector(
    (state: RootState) => state.stepper.fetchUserApplicationData
  );
  const applicationStatus = useSelector(
    (state: RootState) => state.tabs.applicationStatus
  );
  const applicationNumber: any = useSelector(
    (state: RootState) => state.stepper.trackingId
  );
  const submissionTrackingId: any = useSelector(
    (state: RootState) => state.stepper.trackingId
  );
  const aadhaarApplicationNo: any = useSelector(
    (state: RootState) => state.stepper.aadhaarApplicationNo
  );
  const documentApplicantType: any = useSelector(
    (state: RootState) => state.stepper.documentApplicantType
  );
  const formUserData: any = useSelector((state: RootState) => state.stepper.formData);
  const userProfile: any = useSelector((state: RootState) => state.auth.userProfile);
  const isAuthenticated: boolean = useSelector(
    (state: RootState) => state.auth.authStatus
  );
  const normalizedApplicationStatus = String(
    fetchUserApplicationData?.status || applicationStatus || ""
  ).toLowerCase();
  const isSubmittedApplication = [
    "application submitted",
    "submitted",
  ].includes(normalizedApplicationStatus);
  const documentsReadOnly = Boolean(partnerStartedJourney || isSubmittedApplication);

  useEffect(() => {
    if (documentApplicantType === "") {
      dispatch(setDocumentApplicantType("Applicant"));
    }
  }, [dispatch, documentApplicantType]);

  const findCoApplicantNumber: number = useMemo(() => {
    return Number(documentApplicantType?.split(" ")?.[1]);
  }, [documentApplicantType]);

  const documents = useDocumentsData({
    tabIndex,
    applicants,
    formUserData,
    applicationNumber,
    partnerStartedJourney,
    coApplicantDetails,
    documentApplicantType,
    fetchUserApplicationData,
    isAuthenticated,
    useMandatoryDocumentsOnly: hideHeaderActions,
  });

  const preview = useDocumentPreview({ applicationNumber });

  const upload = useDocumentUpload({
    dispatch,
    tabIndex,
    applicants,
    submissionTrackingId,
    applicationNumber,
    applicationId,
    userProfile,
    fetchUserApplicationData,
    coApplicantDetails,
    partnerStartedJourney,
    findCoApplicantNumber,
    formUserData,
    refreshDocuments: documents.getDocumentCategoriesViaAPI,
  });

  const bulk = useBulkUpload({ submissionTrackingId });

  useEffect(() => {
    if (submissionTrackingId) {
      bulk.handleDocCount(submissionTrackingId);
    }
  }, [bulk.handleDocCount, submissionTrackingId]);

  // Fetch consent duration data on load and on every tab switch
  const fetchConsentData = useCallback(async () => {
    if (!submissionTrackingId) return;
    try {
      const response: any = await eligibilityCheckServices.getConsentDurationData(submissionTrackingId);
      if (response?.status === 200 && response?.data?.status === "success") {
        setConsentApiData(response.data.data);
      }
    } catch {
      // silently ignore
    }
  }, [submissionTrackingId]);
// Set aggregatorDocId on page load if not already set and a suitable document exists
  useEffect(() => {
    console.log(documents,"documents")
    if (documents.documentData && Array.isArray(documents.documentData)) {
      // Find the first document with uploadedVia === 'account_agreegator' or any other logic you need
      const aaDoc = documents.documentData.find((doc: any) =>
        doc.name === 'Income Document' || doc.type === 'Account Aggregator' || doc.label?.toLowerCase().includes('account aggregator')
      );
      const data=aaDoc?.documents?.filter((doc: any) => doc?.docName === 'Bank Statement')
      if (data && data[0].id) {
        dispatch(setAggregatorDocId(data[0].id));
      }
    }
  }, [aggregatorDocId, documents.documentData, dispatch]);

  useEffect(() => {
    fetchConsentData();
  }, [fetchConsentData, tabIndex]);

  const refreshCurrentOpenTab = useCallback(async () => {
    await Promise.allSettled([
      fetchConsentData(),
      documents.getDocumentCategoriesViaAPI(),
    ]);
  }, [fetchConsentData, documents]);

  // Derive whether the current tab has a consent ID (controls FI button visibility)
  const hasConsentId = useMemo(() => {
    if (!consentApiData) return false;
    if (tabIndex === 0) {
      console.log(consentApiData,"consentApiData")
      const id = consentApiData?.applicant?.consentId;
      const sessionids = consentApiData?.applicant?.sessionId;
      if(sessionids){
        return false;
      }
      return Boolean(id);
    }
    const coApplicant = consentApiData?.coApplicants?.find(
      (c: any) => parseInt(c?.coApplicantType?.match(/\d+/)?.[0] ?? "0") === tabIndex
    );
    const id = coApplicant?.consentId
    const sessionid = coApplicant?.sessionId;
    if(sessionid){
      return false;
    }
    return Boolean(id);
  }, [consentApiData, tabIndex]);

  // Derive whether session ID exists (controls Get FI Data button visibility)
  const hasSessionId = useMemo(() => {
    if (fiSessionId) return true;
    if (!consentApiData) return false;
    if (tabIndex === 0) {
      const sid = consentApiData?.applicant?.sessionId
      return Boolean(sid);
    }
    const coApplicant = consentApiData?.coApplicants?.find(
      (c: any) => parseInt(c?.coApplicantType?.match(/\d+/)?.[0] ?? "0") === tabIndex
    );
    const sid = coApplicant?.sessionId;
    return Boolean(sid);
  }, [consentApiData, tabIndex, fiSessionId]);

  useAccountAggregator({
    dispatch,
    navigate,
    locationSearch: location.search,
    applicationNumber,
    tabIndex,
    userProfile,
    aggregatorDocId,
    consentApplicationNo,
    setAggregatorLoader,
    refreshDocuments: documents.getDocumentCategoriesViaAPI,
  });

  useDigiLocker({
    dispatch,
    navigate,
    locationSearch: location.search,
    tabIndex,
    applicants,
    aadhaarApplicationNo,
    formUserData,
    documentApplicantType,
    submissionTrackingId,
    setAggregatorLoader,
    refreshDocuments: documents.getDocumentCategoriesViaAPI,
  });

  const coApplicant = useCoApplicantFlow({
    dispatch,
    applicants,
    tabIndex,
    setTabIndex,
    applicationNumber,
    submissionTrackingId,
    userProfile,
    fetchUserApplicationData,
    applicationId,
    handleFetchApplicationDetail,
    refreshDocuments: documents.getDocumentCategoriesViaAPI,
    setPartnerStartedJourney,
  });

  const handleChange = useCallback(
    async (_event: React.SyntheticEvent, newValue: number) => {
      localStorage.removeItem("coAppId");
      dispatch(setDocumentApplicantType(newValue === 0 ? "Applicant" : applicants?.[newValue]));
      setTabIndex(newValue);

      if (newValue > 0) {
        try {
          const payload = {
            trackingId: applicationNumber,
            coApplicantType: applicants?.[newValue],
          };
          const response: any = await losServices.getCoApplicantDetails(payload);
          if (response?.status === 200 && response?.data?.data) {
            try {
              coApplicant.setDeleteFieldIds(
                JSON.parse(decrypt(response?.data?.data?.storeData))
              );
            } catch {
              coApplicant.setDeleteFieldIds([]);
            }

            coApplicant.setCoApplicantFields(response?.data?.data?.formData);
            dispatch(setDocumentApplicantType(response?.data?.data?.coApplicantType));

            if (response?.data?.data?.applicantId != null) {
              localStorage.setItem("coAppId", response?.data?.data?.applicantId);
            } else {
              localStorage.setItem("coAppId", "0");
            }
          } else {
            // API success but no/empty data – set defaults so flow continues
            coApplicant.setDeleteFieldIds([]);
            coApplicant.setCoApplicantFields([]);
            localStorage.setItem("coAppId", "0");
          }
        } catch (_error: any) {
          // API fail – set defaults so flow never breaks; tab switch already done
        }
      }
    },
    [applicationNumber, applicants, coApplicant, dispatch, documents]
  );

  const handleViewBulkDocuments = useCallback(async () => {

    navigate("/assisted-portal/bulk-documents", { state: { tabIndex } });

    // const trackingId = String(applicationNumber ?? "").trim();
    // if (!trackingId) {
    //   dispatch(
    //     setStatusPopup({
    //       status: "error",
    //       message: "Tracking ID is not available. Please try again later.",
    //     })
    //   );
    //   return;
    // }
    // try {
    //   setViewBulkDocumentsLoader(true);
    //   const response = await eligibilityCheckServices.viewDocs({ leadId: trackingId });
    //   const redirectUrl =
    //     response?.data?.data?.data
    //   if (redirectUrl) {
    //     window.location.href = toAbsoluteUrl(redirectUrl);
    //   } else {
    //     dispatch(
    //       setStatusPopup({
    //         status: "error",
    //         message:
    //           response?.data?.message ||
    //           "Redirect link not received. Please try again.",
    //       })
    //     );
    //   }
    // } catch (error: any) {
    //   dispatch(
    //     setStatusPopup({
    //       status: "error",
    //       message:
    //         error?.response?.data?.message ||
    //         "Failed to open bulk documents. Please try again.",
    //     })
    //   );
    // } finally {
    //   setViewBulkDocumentsLoader(false);
    // }

  }, [dispatch, applicationNumber]);

  const handleViewCustomerUploadDocuments = useCallback(() => {
    navigate(ROUTES.Application.customerUploadDocuments.locationPath);
  }, [navigate]);

  // Helper function to extract applicant data from API response
  const getApplicantData = useMemo(() => {
    if (partnerStartedJourney) {
      // If partner started journey, use mainApplicantDetails from handleFetchPartnerLeadDetailsApi
      const result = (mainApplicantDetails || []).reduce((acc: any, item: any) => {
        if (["firstName", "lastName", "email", "phone_number"].includes(item.name)) {
          acc[item.name] = item.value;
        }
        return acc;
      }, {});
      return {
        email: result?.email || "",
        name: result?.firstName && result?.lastName
          ? `${result.firstName} ${result.lastName}`
          : "",
        phoneNumber: result?.phone_number || "",
      };
    }

    // If partner started journey is false, extract from fetchUserApplicationData -> Eligibility Check -> verticalSteps
    let email = "";
    let firstName = "";
    let lastName = "";
    let phoneNumber = "";

    if (fetchUserApplicationData?.horizontalSteps?.[0]?.verticalSteps) {
      const eligibilityCheckVerticalSteps = fetchUserApplicationData.horizontalSteps[0].verticalSteps;

      // Iterate through all verticalSteps to find fields
      for (const verticalStep of eligibilityCheckVerticalSteps) {
        if (verticalStep?.fields && Array.isArray(verticalStep.fields)) {
          for (const field of verticalStep.fields) {
            if (field?.email) {
              email = field.email;
            }
            if (field?.firstName) {
              firstName = field.firstName;
            }
            if (field?.lastName) {
              lastName = field.lastName;
            }
            if (field?.phone_number) {
              phoneNumber = field.phone_number;
            }
          }
        }
      }
    }

    return {
      email: email || "",
      name: firstName && lastName ? `${firstName} ${lastName}` : "",
      phoneNumber: phoneNumber || "",
    };
  }, [partnerStartedJourney, mainApplicantDetails, fetchUserApplicationData]);

  const handleTriggerLinkToCollectDocuments = useCallback(
    async (tabIndex: number, type: "applicant" | "co-applicant") => {
      try {
        const payload = {
          email:
            type === "applicant"
              ? getApplicantData.email
              : coApplicantDetails[
                Number(documentApplicantType?.split("Co-Applicant ")?.[1])
              ]?.Email,
          name:
            type === "applicant"
              ? getApplicantData.name
              : coApplicantDetails[
                Number(documentApplicantType?.split("Co-Applicant ")?.[1])
              ]?.FirstName +
              " " +
              coApplicantDetails[
                Number(documentApplicantType?.split("Co-Applicant ")?.[1])
              ]?.LastName,
          mobileNumber:
            type === "applicant"
              ? getApplicantData.phoneNumber
              : coApplicantDetails[
                Number(documentApplicantType?.split("Co-Applicant ")?.[1])
              ]?.PhoneNumber,
          submissionTrackingId,
          toUser: type === "applicant",
        };

        const response =
          await eligibilityCheckServices.triggerLinkToCollectDocuments(payload);
        if (response?.status === 200) {


          trackCustomerJourneyEvent(
            `ASSISTED_JOURNEY_SEND_LINK_TO_CUSTOMER`,
            isAuthenticated ? decrypt(userProfile?.id) ?? "" : "",
            applicationNumber,
            {
              label: `ASSISTED_JOURNEY_SEND_LINK_TO_CUSTOMER`,
              category: `ASSISTED_JOURNEY_SEND_LINK_TO_CUSTOMER`,
              action: `ASSISTED_JOURNEY_SEND_LINK_TO_CUSTOMER`,
              page: "Send Link to Customer",
            }
          );

          dispatch(
            setStatusPopup({
              status: "success",
              message: response?.data?.message,
            })
          );
        }
      } catch (error: any) {
        dispatch(
          setStatusPopup({
            status: "error",
            message: error?.response?.data?.message,
          })
        );
      }
    },
    [
      coApplicantDetails,
      dispatch,
      documentApplicantType,
      submissionTrackingId,
      getApplicantData,
    ]
  );

  const handleShowAddMoreDocument = useCallback(
    (id: number) => {
      setIsAddMoreModalOpen(true);
      documents.handleGetCategoryList(id);
      setModelType("addSubDocument");
    },
    [documents]
  );

  const locationPath = useLocation().pathname;
  const getValidatedToDate = (toDateString: string | null | undefined): string => {
    const formatDate = (d: string) =>
            d ? (d.includes("T") ? d : d + "T00:00:00Z") : "";
    if (!toDateString) return formatDate(new Date().toISOString());

    const targetDate = new Date(toDateString);
    const currentDate = new Date();

    // If the parsed date is invalid, default to today
    if (isNaN(targetDate.getTime())) {
      return formatDate(currentDate.toISOString());
    }

    // Check if targetDate is greater than the current date
    if (targetDate > currentDate) {
      return formatDate(currentDate.toISOString());
    }

    // Otherwise, return the original formatted date string
    return formatDate(toDateString);
  };
  const handleCreateFIData = useCallback(
    async (_item: any) => {
      try {
        setAggregatorLoader(true);

        // Fetch fresh consent duration data right before calling the API
        let consentRecord: any = null;
        try {
          const consentResponse: any = await eligibilityCheckServices.getConsentDurationData(submissionTrackingId);
          if (consentResponse?.status === 200 && consentResponse?.data?.status === "success") {
            const data = consentResponse.data.data;
            setConsentApiData(data);
            consentRecord = tabIndex === 0
              ? data?.applicant
              : data?.coApplicants?.find(
                  (c: any) => parseInt(c?.coApplicantType?.match(/\d+/)?.[0] ?? "0") === tabIndex
                );
          }
        } catch {/* ignore – proceed with empty payload */}

        const formatDate = (d: string) =>
          d ? (d.includes("T") ? d : d + "T00:00:00Z") : "";

        const payload = {
          consentId:
            consentRecord?.consentId ||
            consentRecord?.utilityAaAppNo ||
            consentRecord?.aaConsentAppNo ||
            "",
          fromDate: formatDate(consentRecord?.aaFromDate ?? ""),
          toDate: getValidatedToDate(consentRecord?.aaToDate ?? ""),
        };

        const response: any = await eligibilityCheckServices.consentFIRequest(payload);
        if (response?.status === 200 || response?.status === 201) {
          const sessionId =
            response?.data?.data?.sessionId ||
            response?.data?.data?.id ||
            "";
          setFiSessionId(sessionId);
          await refreshCurrentOpenTab();
          dispatch(
            setStatusPopup({
              status: "success",
              message: response?.data?.message || "FI data request created successfully.",
            })
          );
        }
      } catch (error: any) {
        dispatch(
          setStatusPopup({
            status: "error",
            message:
              error?.response?.data?.message || "Failed to create FI data request.",
          })
        );
      } finally {
        setAggregatorLoader(false);
      }
    },
    [dispatch, tabIndex, submissionTrackingId, refreshCurrentOpenTab]
  );

  const handleGetFIData = useCallback(
    async (_item: any) => {
      try {
        setAggregatorLoader(true);

        // Fetch fresh consent duration data right before calling the API
        let consentRecord: any = null;
        try {
          const consentResponse: any = await eligibilityCheckServices.getConsentDurationData(submissionTrackingId);
          if (consentResponse?.status === 200 && consentResponse?.data?.status === "success") {
            const data = consentResponse.data.data;
            setConsentApiData(data);
            consentRecord = tabIndex === 0
              ? data?.applicant
              : data?.coApplicants?.find(
                  (c: any) => parseInt(c?.coApplicantType?.match(/\d+/)?.[0] ?? "0") === tabIndex
                );
          }
        } catch {/* ignore – proceed with stored fiSessionId */}

        const sessionId = fiSessionId || consentRecord?.sessionId || "";
          const applicationNo=
          consentRecord?.applicationNo ||
          "";

        // Prepare payload for POST
        const fiPayload = {
          trackingId: submissionTrackingId,
          docId: aggregatorDocId.toString(), // Set this to the correct docId if available in your context
          typeOfApplicant: tabIndex === 0 ? "Applicant" : `Co-Applicant ${findCoApplicantNumber}`,
          applicationNo: applicationNo,
          sessionId: sessionId,
        };
        const response: any = await eligibilityCheckServices.getFIRequest(sessionId, applicationNo);
        if (response?.status === 200) {
          await refreshCurrentOpenTab();
          dispatch(
            setStatusPopup({
              status: "success",
              message: response?.data?.message || "FI data fetched successfully.",
            })
          );
        }
      } catch (error: any) {
        dispatch(
          setStatusPopup({
            status: "error",
            message:
              error?.response?.data?.message || "Failed to get FI data.",
          })
        );
      } finally {
        setAggregatorLoader(false);
      }
    },
    [
      dispatch,
      fiSessionId,
      tabIndex,
      submissionTrackingId,
      aggregatorDocId,
      findCoApplicantNumber,
      refreshCurrentOpenTab,
    ]
  );

  const handleViewDocumentItem = useCallback(
    async (item: any, index?: number) => {
      let val = index ? index : 0
      const docId = item?.id;
      setViewDocumentLoader((prev) => (docId != null ? [...prev, docId] : prev));
      try {
        const docUrl = item?.userDocs?.[val]?.docUrl;
        const docUuid = item?.userDocs?.[val]?.uuid;
        const s3Location = item?.userDocs?.[val]?.s3Location;
        const uploadedtype: string =
          item?.userDocs[val]?.uploadedVia || upload.uploadedVia;
        // Prefer UUID-based preview whenever available.
        if ((docUuid || partnerStartedJourney || s3Location) && locationPath !== ROUTES.Application.customerUploadDocuments.locationPath) {
          await preview.handlePreviewDownloadDocument(docUuid, s3Location);
          return;
        }

        if (!docUrl) {
          dispatch(
            setStatusPopup({
              status: "error",
              message: "Document preview is not available for this file.",
            })
          );
          return;
        }

        if (uploadedtype === "account_agreegator") {
          await preview.handlePreviewAggregateDocument(docUrl);
        } else {
          await preview.handlePreviewDocument(docUrl);
        }
      } finally {
        setViewDocumentLoader((prev) =>
          docId != null ? prev.filter((id) => id !== docId) : prev
        );
      }
    },
    [dispatch, preview, upload.uploadedVia, location.pathname, partnerStartedJourney]
  );

  const headerActionsContent = !hideHeaderActions && !headerActionsOnly
    ? (isApplicantTabsLoading || tabsListLoading ? (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Skeleton variant="text" width={120} height={32} />
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
            {[1, 2, 3, 4].map((item) => (
              <Skeleton
                key={item}
                variant="rounded"
                width={isXsOrSm ? "100%" : 190}
                height={38}
                sx={{ borderRadius: "8px" }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    ) : (
      <ApplicantTabsHeaderActions
        isXsOrSm={isXsOrSm}
        tabIndex={tabIndex}
        countBulkUpload={bulk.countBulkUpload}
        isAddingCoApplicant={coApplicant.isAddingCoApplicant}
        enableCustomerJourney={enableCustomerJourney}
          partnerStartedJourney={partnerStartedJourney}
          documentsReadOnly={documentsReadOnly}
          onAddCoApplicant={coApplicant.handleGetCoApplicantCount}
          onTriggerLinkToCustomer={() =>
            handleTriggerLinkToCollectDocuments(tabIndex, "applicant")
          }
        onViewBulkDocuments={handleViewBulkDocuments}
          onViewCustomerUploadDocuments={handleViewCustomerUploadDocuments}
          onOpenBulkUploadModal={bulk.openBulkUploadModal}
          hideTriggerLink={Boolean(headerActionsPortalTarget)}
        />
    ))
    : null;

  return (
    <>
      {!headerActionsOnly && (
        <Paper sx={{ p: isXsOrSm ? 2 : 3, mb: 3 }}>
          {(aggregatorLoader || viewBulkDocumentsLoader) && (
            <LoaderBackdrop open={aggregatorLoader || viewBulkDocumentsLoader} />
          )}

          {headerActionsContent}

        <ApplicantTabsNav
          tabIndex={tabIndex}
          applicants={applicants}
          isXsOrSm={isXsOrSm}
          tabsListLoading={tabsListLoading}
          isApplicantTabsLoading={isApplicantTabsLoading}
          onChange={handleChange}
        />

        {documents.isLoading ? (
          <DocumentsSkeletonPanel tabIndex={tabIndex} />
        ) : (
          <TabPanel value={tabIndex} index={tabIndex}>
            <ApplicantInfoBox
              isXsOrSm={isXsOrSm}
              tabIndex={tabIndex}
              userProfile={userProfile}
              coApplicantDetails={coApplicantDetails}
              partnerStartedJourney={partnerStartedJourney}
              documentApplicantType={documentApplicantType}
              onTriggerLinkToCollectDocuments={() =>
                handleTriggerLinkToCollectDocuments(tabIndex, "co-applicant")
              }
              mainApplicantDetails={mainApplicantDetails}
              fetchUserApplicationData={fetchUserApplicationData}
              applicants={applicants}
              hideHeaderActions={hideHeaderActions}
              documentsReadOnly={documentsReadOnly}
            />

            <DocumentsSectionHeader
              tabIndex={tabIndex}
              partnerStartedJourney={partnerStartedJourney}
              onConfirmDeleteCoApplicant={coApplicant.handleConfirmDeleteCoApplicant}
            />

            <DocumentCategoriesList
              documentData={documents.documentData}
              isXsOrSm={isXsOrSm}
              partnerStartedJourney={partnerStartedJourney}
              hideAddButton={hideHeaderActions || documentsReadOnly}
              hideUploadButton={hideHeaderActions || documentsReadOnly}
              hideAccountAggregator={hideHeaderActions || documentsReadOnly}
              uploadDocumentLoader={upload.uploadDocumentLoader}
              digiLockerLoaderId={digiLockerLoaderId}
              viewDocumentLoader={viewDocumentLoader}
              uploadedVia={upload.uploadedVia}
              onShowAddMoreDocument={handleShowAddMoreDocument}
              onUploadDocument={upload.handleUploadDocument}
              onViewDocument={handleViewDocumentItem}
              onOpenAccountAggregatorModal={() => setIsAccountAggregatorModalOpen(true)}
              onCreateFIData={handleCreateFIData}
              onGetFIData={handleGetFIData}
              showFIButtons={hasConsentId}
              showGetFIButton={hasSessionId}
            />

            {!partnerStartedJourney && !hideHeaderActions && !documentsReadOnly && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setIsAddMoreModalOpen(true);
                  documents.setMasterDocumentList([]);
                  documents.handleGetCategoryList(0);
                  setModelType("addMainDocument");
                }}
                sx={{
                  my: 2,
                  bgcolor: "#00b3b5",
                  color: "#fff",
                  "&:hover": { bgcolor: "#00999a" },
                  textTransform: "none",
                  px: 3,
                  py: 1,
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Add More Documents
              </Button>
            )}



            {isXsOrSm && <CustomDivider />}
          </TabPanel>
        )}

        </Paper>
      )}
      {!hideHeaderActions &&
        !disableHeaderActionsPortal &&
        headerActionsPortalTarget &&
        createPortal(
          isApplicantTabsLoading || tabsListLoading ? (
            <Skeleton
              variant="rounded"
              width={190}
              height={39}
              sx={{ borderRadius: "6px" }}
            />
          ) : (
            <ApplicantTabsHeaderActions
              isXsOrSm={isXsOrSm}
              tabIndex={tabIndex}
              countBulkUpload={bulk.countBulkUpload}
              isAddingCoApplicant={coApplicant.isAddingCoApplicant}
              enableCustomerJourney={enableCustomerJourney}
              partnerStartedJourney={partnerStartedJourney}
              documentsReadOnly={documentsReadOnly}
              onAddCoApplicant={coApplicant.handleGetCoApplicantCount}
              onTriggerLinkToCustomer={() =>
                handleTriggerLinkToCollectDocuments(tabIndex, "applicant")
              }
              onViewBulkDocuments={handleViewBulkDocuments}
              onViewCustomerUploadDocuments={handleViewCustomerUploadDocuments}
              onOpenBulkUploadModal={bulk.openBulkUploadModal}
              showTitle={false}
              triggerOnly
              primaryTriggerLink
            />
          ),
          headerActionsPortalTarget
        )}
      <ApplicantTabsModals
        isXsOrSm={isXsOrSm}
        tabIndex={tabIndex}
        applicants={applicants}
        applicationNumber={applicationNumber}
        bulk={bulk}
        documents={documents}
        preview={preview}
        upload={upload}
        coApplicant={coApplicant}
        isAddMoreModalOpen={isAddMoreModalOpen}
        setIsAddMoreModalOpen={setIsAddMoreModalOpen}
        modelType={modelType}
        setModelType={setModelType}
        isAccountAggregatorModalOpen={isAccountAggregatorModalOpen}
        setIsAccountAggregatorModalOpen={setIsAccountAggregatorModalOpen}
        hideAccountAggregator={hideHeaderActions}
      />
    </>
  );
}
